import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthUtils } from 'app/fuse-template/core/auth/auth.utils';
import { UserService } from 'app/fuse-template/core/user/user.service';
import { firstAndMiddleNameUser } from 'app/helpers/firstAndMiddleNameUser';
import { ExposedAccountsLoginReq } from 'app/interfaces/requests/Exposed/exposedAccountsLoginReq';
import { ExposedLoginRequestStatusReq } from 'app/interfaces/requests/Exposed/exposedLoginRequestStatusReq';
import { ExposedAccountsLoginRes } from 'app/interfaces/responses/Exposed/exposedAccountsLoginRes';
import { ExposedInitiateLoginRes } from 'app/interfaces/responses/Exposed/exposedInitiateLoginRes';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { LoginStatus } from 'common/constants/login-status.constants';
import { environment } from 'environments/environment';
import { catchError, map, Observable, of, Subject, switchMap, tap } from 'rxjs';

@Injectable()
export class AuthService {
    public _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private lsrauthService: LsrauthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    get accessToken(): string {
        try {
            return localStorage.getItem('accessToken') ?? '';
        } catch {
            return '';
        }
    }

    set accessToken(token: string) {
        try {
            localStorage.setItem('accessToken', token);
        } catch {
            localStorage.setItem('accessToken', '');
        }
    }

    /**
     * Setter & getter for time of access token
     */
    get _lastTokenTime(): Date {
        try {
            return new Date(localStorage.getItem('accessTokenTimeGenerated')) ?? null;
        } catch {
            return null;
        }
    }

    set _lastTokenTime(date) {
        try {
            localStorage.setItem('accessTokenTimeGenerated', date.toString());
        } catch {
            localStorage.removeItem('accessTokenTimeGenerated');
        }
    }

    /**
     * Setter & getter for refresh token
     */
    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        try {
            return localStorage.getItem('refreshToken') ?? '';
        } catch {
            return '';
        }
    }

    /**
     * Setter & getter for phone
     */
    set phone(phone: string) {
        localStorage.setItem('phone', phone);
    }

    get phone(): string {
        try {
            return localStorage.getItem('phone') ?? '';
        } catch {
            return '';
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param req
     */
    signIn(req: { SocialSecurityNumber: string; Phonenumber: string; HashString: string }): Observable<any> {
        return this._httpClient.post(environment.localApi + 'auth/login', req).pipe(
            switchMap((response: HttpStatus<ExposedAccountsLoginRes>) => {
                let name = firstAndMiddleNameUser(response.returnItem.nafn);

                localStorage.setItem('rememberMeName', name);

                // Store the access token in the local storage
                this.accessToken = response.returnItem.accessToken;
                this.refreshToken = response.returnItem.refreshToken;
                this._lastTokenTime = new Date();

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.returnItem;

                // Fetches details regarding the user
                this.lsrauthService.fetchUserDetails().subscribe((res) => {});

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    public initiateLogin(req: ExposedAccountsLoginReq): Observable<ExposedInitiateLoginRes> {
        return this._httpClient.post<HttpStatus<ExposedInitiateLoginRes>>(environment.localApi + 'auth/login/initiate', req).pipe(
            map((res: HttpStatus<ExposedInitiateLoginRes>) => {
                return res.returnItem;
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    public checkLoginStatus(req: ExposedLoginRequestStatusReq): Observable<ExposedAccountsLoginRes | null> {
        return this._httpClient.post<HttpStatus<ExposedAccountsLoginRes>>(environment.localApi + 'auth/login/status', req).pipe(
            tap((res: HttpStatus<ExposedAccountsLoginRes>): void => {
                if (res.returnItem.loginStatus == LoginStatus.Pending) {
                    return;
                }
                let name = firstAndMiddleNameUser(res.returnItem.nafn);

                localStorage.setItem('rememberMeName', name);

                // Store the access token in the local storage
                this.accessToken = res.returnItem.accessToken;
                this.refreshToken = res.returnItem.refreshToken;
                this._lastTokenTime = new Date();

                // Store the user on the user service
                this._userService.user = res.returnItem;

                // Fetches details regarding the user
                this.lsrauthService.fetchUserDetails().subscribe((res) => {});
            }),
            map((res: HttpStatus<ExposedAccountsLoginRes>) => {
                return res.returnItem;
            }),
            catchError((error) => {
                console.log('Error');
                throw error;
            })
        );
    }

    getVerificationCode(req: { HashString: string }): Observable<any> {
        return this._httpClient.get(environment.localApi + 'auth/login/verificationCode/' + req.HashString).pipe(
            switchMap((response: HttpStatus<string>) => {
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        return this._httpClient.get(environment.localApi + 'auth/refresh/' + this.refreshToken).pipe(
            catchError(() =>
                // Return false
                of(false)
            ),
            switchMap((response: HttpStatus<ExposedAccountsLoginRes>) => {
                try {
                    // Store the access token in the local storage
                    this.accessToken = response.returnItem.accessToken;
                    this.refreshToken = response.returnItem.refreshToken;
                    this._lastTokenTime = new Date();

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = response.returnItem;

                    // Fetches details regarding the user
                    this.lsrauthService.fetchUserDetails().subscribe((res) => {});

                    // Return true
                    return of(true);
                } catch {
                    return of(false);
                }
            })
        );
    }

    updateToken(): Observable<HttpStatus<ExposedAccountsLoginRes>> {
        let subject = new Subject<HttpStatus<ExposedAccountsLoginRes>>();

        this._httpClient
            .get(environment.localApi + 'auth/refresh/' + this.refreshToken)
            .subscribe(
                (res: HttpStatus<ExposedAccountsLoginRes>) => {
                    // Store the access token in the local storage
                    this.accessToken = res.returnItem.accessToken;
                    this.refreshToken = res.returnItem.refreshToken;
                    this._lastTokenTime = new Date();

                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the user on the user service
                    this._userService.user = res.returnItem;

                    this.lsrauthService.fetchUserDetails().subscribe(() => {});

                    subject.next(res);
                },
                (error) => {
                    subject.next(null);
                }
            )
            .add(() => {});
        return subject.asObservable();
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        this._lastTokenTime = null;

        // Removes user details
        this.lsrauthService.changeUserDetails(null);

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        try {
            // Check if the user is logged in
            if (this._authenticated) {
                return of(true);
            }

            // Check the access token availability
            if (!this.accessToken) {
                return of(false);
            }

            // Check the access token expire date
            if (AuthUtils.isTokenExpired(this.accessToken)) {
                return of(false);
            }

            // If the access token exists and it didn't expire, sign in using it
            return this.signInUsingToken();
        } catch {
            this.accessToken = null;
            return of(false);
        }
    }
}
